import { useCallback, useEffect, useRef, useState } from "react";
import { Platform, Pressable, View } from "react-native";
import { CameraOff } from "lucide-react-native";

import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import type {
  InjectionProtocol,
  ProtocolSite,
  ProtocolStep,
} from "@src/packages/injection-protocol/types";

import { cameraRoleFor } from "../cameraRole";
import { kneePointsTo, type ThighSide } from "../thighSide";
import { figureForStep } from "../overlay/figures";
import type { PoseReading } from "../pose/types";
import {
  EMPTY_TRACKER,
  framingFrom,
  injectionZonesFrom,
  LIMB_TRACKING,
  thighZoneFrom,
  TORSO_TRACKING,
  trackZones,
  type Framing,
  type InjectionZones as Zones,
  type ZoneTracker,
} from "../pose/zones";
import { FramingSilhouette } from "./overlay/FramingSilhouette";
import { InjectionZones } from "./overlay/InjectionZones";
import { CameraPreview, PREVIEW_DETECTS_POSE } from "./CameraPreview";

const ns = "care.injection_coach";

/**
 * Her own camera, mirrored, with the step's diagram drawn over it.
 *
 * ### What this component is not responsible for
 *
 * Getting the picture. `CameraPreview` does that, and it is split by platform
 * because the two have nothing in common: native renders `expo-camera`, web
 * opens its own stream so a pose model can see the frames. Only `PoseReading`
 * comes back across, so no video element and no DOM reaches this file.
 *
 * ### Why the overlay is a sibling, not a child
 *
 * The diagram must **not** be mirrored — flipping the composition would reverse
 * every figure, and a reversed diagram of where to inject is worse than none.
 * The web build happens to render children outside the flipped element, but
 * relying on that would make correctness depend on a detail of someone else's
 * render tree. So the overlay is absolutely positioned over the camera rather
 * than nested inside it, and the separation holds on every platform by
 * construction.
 *
 * ### What is drawn over the picture
 *
 * The injection area, outlined on her own body — or nothing at all. There is no
 * third option, on purpose. A diagram of somebody's abdomen floating over hers
 * was the thing that made this confusing to use, and a fallback drawing is
 * exactly what ends up on screen most of the time. When the camera cannot
 * support an outline the mirror stands on its own, and the words above it carry
 * the step. See `pose/zones.ts`.
 *
 * ### Turning it off
 *
 * The disclosure tells her she can turn the camera off at any moment. That
 * claim was vacuously true while there was no camera; it stops being vacuous
 * here, so the control is part of this component rather than something a host
 * might remember to add.
 */
export function InjectionCoachCameraStage({
  step,
  protocol,
  phase,
  site,
  leg,
  onTurnOff,
  onFraming,
  onCannotOutline,
}: {
  /** The step she is on. Decides which diagram is drawn. */
  step: ProtocolStep;
  /**
   * The procedure being walked, which decides what the camera is *for*.
   *
   * A subcutaneous abdominal dose gets the outline on her body; anything else
   * gets a mirror and nothing drawn over it. See `cameraRole.ts`.
   */
  protocol: InjectionProtocol;
  /**
   * What the preview is for right now.
   *
   * `framing` is the moment before the injection where she points the phone at
   * her abdomen; `guiding` is the injection itself. One component covers both
   * so the stream is opened once — remounting between them would drop the
   * camera and re-check the permission at the worst possible moment.
   */
  phase: "framing" | "guiding";
  /**
   * The site she chose, where the procedure offered a choice.
   *
   * Passed rather than derived, and the reason is the whole point of the
   * outline: the pose layer reads a torso, so it can place an area on an
   * abdomen and on nothing else. Without this the stage computed its role from
   * the route alone and drew the abdominal area, confidently, over a thigh.
   */
  site?: ProtocolSite["id"] | null;
  /**
   * The leg she named, where the site is a thigh.
   *
   * Null on an abdomen, and null on a thigh only in the instant before she has
   * answered — the walk asks before it shows this. Both the outline she matches
   * herself to and the band drawn on her body read it.
   */
  leg?: ThighSide | null;
  /** She turned it off, or it could not start. Either way, stop asking for it. */
  onTurnOff: () => void;
  /** How well she is framed, reported while `phase` is `framing`. */
  onFraming?: (framing: Framing) => void;
  /**
   * No outline will be placed on her body in this session — this platform has
   * no detector, or the model would not load.
   *
   * Reported because the card above suppresses its own diagram while a camera
   * is on, and it has no other way to learn that the camera is not drawing one.
   * Stated once, as a capability, rather than every time she steps out of frame.
   */
  onCannotOutline?: () => void;
}) {
  const t = useTranslations();
  const [failed, setFailed] = useState(false);

  const figure = figureForStep(step);

  /**
   * The shape of this box, held in a ref as well as in state.
   *
   * Needed because the preview fills the box with `cover`: unless the frame and
   * the box share an aspect ratio the frame is cropped, and a landmark at the
   * middle of it is not at the middle of the box. The ref is what the reading
   * handler reads — it is created once and would otherwise close over the first
   * render's value, which is zero.
   */
  const boxAspectRef = useRef(0);

  /**
   * A mirror, or a guide.
   *
   * On a mirror nothing is detected and nothing is drawn, so the card above
   * keeps its own diagram and the framing gate never appears — there is no
   * observation to report and no frame to get right. Reported upward on mount
   * for the same reason a phone with no detector reports it: the card must not
   * hide its figure in favour of an outline that is not coming.
   */
  const guiding = cameraRoleFor(protocol, site) === "guide";
  // Read inside the detection callback, which is created once.
  const guidingRef = useRef(guiding);
  guidingRef.current = guiding;
  // Same reason as `guidingRef`: the detection loop must not be torn down and
  // restarted every time this component re-renders, so what it needs to read is
  // read from a ref rather than closed over.
  const siteRef = useRef(site);
  siteRef.current = site;
  // Same reason as the site: the detector callback is created once and would
  // otherwise close over the leg she had named when the camera started.
  const legRef = useRef(leg);
  legRef.current = leg;

  /**
   * What to draw, already smoothed and held.
   *
   * Tracked as readings arrive rather than derived from the latest one. Raw
   * output shivers — landmarks wander a few pixels every frame, twelve times a
   * second — and one dropped detection used to blink the outlines off and back
   * on. Both of those read as the overlay never settling, which is exactly what
   * they are. See `trackZones`.
   */
  const [zones, setZones] = useState<Zones | null>(null);
  const trackerRef = useRef<ZoneTracker>(EMPTY_TRACKER);
  /**
   * Starts at what is true before anything has happened: either this platform
   * cannot detect at all, or a detector exists and has not answered yet.
   * Neither is "we can't see you" — that is a claim about her.
   */
  const [framing, setFraming] = useState<Framing>(
    PREVIEW_DETECTS_POSE && guiding ? "starting" : "unavailable",
  );

  // A camera that cannot start is not an error she should have to solve. The
  // walk works without it, so the stage reports out and disappears rather than
  // leaving a black rectangle above the step she is trying to read.
  const handleMountError = useCallback(() => {
    setFailed(true);
    onTurnOff();
  }, [onTurnOff]);

  // Stable, so the preview's detection loop is not torn down and restarted
  // every time this component re-renders — which, once readings arrive, is
  // several times a second.
  // The model could not load. The camera is untouched and she still sees
  // herself; there is simply nothing to place, which is the same position every
  // phone is in.
  const onCannotOutlineRef = useRef(onCannotOutline);
  onCannotOutlineRef.current = onCannotOutline;
  const handleDetectorUnavailable = useCallback(() => {
    setFraming("unavailable");
    onCannotOutlineRef.current?.();
  }, []);

  // Said once, on mount, for a platform that was never going to place one —
  // every phone. The web says it later, if the model fails; native has nothing
  // to wait for and the card above should not wait either.
  useEffect(() => {
    if (!PREVIEW_DETECTS_POSE || !guiding) onCannotOutlineRef.current?.();
  }, [guiding]);

  /**
   * Waiting has a limit.
   *
   * `starting` only ever ended on a reading or on an outright failure, and
   * neither arrives when the model is merely slow — 28 MB of WebAssembly on a
   * bad connection, or an autoplay policy that never lets the video report
   * itself ready. She was left reading "Starting the camera guide…" with the
   * continue button de-emphasised, mid-procedure, with nothing to wait for that
   * anyone had promised. Six seconds, then the honest answer. A reading that
   * arrives later overwrites it and the guide appears after all.
   */
  useEffect(() => {
    if (framing !== "starting") return;
    const giveUp = setTimeout(() => setFraming("unavailable"), 6000);
    return () => clearTimeout(giveUp);
  }, [framing]);

  const handleReading = useCallback(
    (next: PoseReading, frameAspect: number) => {
      // A mirror asks nothing of the detector, so a reading it happens to
      // receive decides nothing either.
      if (!guidingRef.current) return;
      // Which outline, decided by the site she chose: an area between her
      // shoulders and hips, or a band between a hip and a knee. Both are
      // smoothed by the same tracker, because both shiver for the same reason.
      // The thigh's is additionally fenced by the drawn outline — it follows
      // her leg, and it does not follow a mis-detected knee off her leg.
      const measured =
        siteRef.current === "thigh"
          ? thighZoneFrom(
              next,
              frameAspect,
              boxAspectRef.current,
              legRef.current,
            )
          : injectionZonesFrom(next, frameAspect, boxAspectRef.current);
      // A limb is held calmer than a torso: its centre is a midpoint of two
      // landmarks rather than four, one of them a knee, so the jitter that
      // cancels on an abdomen lands on the drawing here. See `LIMB_TRACKING`.
      const tracked = trackZones(
        trackerRef.current,
        measured,
        siteRef.current === "thigh" ? LIMB_TRACKING : TORSO_TRACKING,
      );
      trackerRef.current = tracked;
      setZones(tracked.zones);
      // From the reading itself rather than from the tracked outline: the hold
      // is there to stop the drawing flickering, and telling her she is framed
      // for half a second after she is not would be the same lie the
      // observation rule exists to prevent.
      setFraming(
        framingFrom(
          next,
          siteRef.current === "thigh" ? "thigh" : "torso",
          legRef.current,
        ),
      );
    },
    [],
  );

  /**
   * Framing reported upward, only when it is asked for and only when it changes.
   *
   * Readings arrive twelve times a second and the answer is one of three words,
   * so calling on every reading would re-render the walk twelve times a second
   * to say the same thing.
   */
  const onFramingRef = useRef(onFraming);
  onFramingRef.current = onFraming;
  useEffect(() => {
    if (phase !== "framing") return;
    onFramingRef.current?.(framing);
  }, [phase, framing]);

  if (failed) return null;

  return (
    <View
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        if (width > 0 && height > 0) boxAspectRef.current = width / height;
      }}
      style={{
        width: "100%",
        aspectRatio: 3 / 4,
        maxHeight: 340,
        borderRadius: 22,
        overflow: "hidden",
        backgroundColor: "#1C120E",
        marginBottom: 14,
      }}
    >
      {/* No reading is asked for on a mirror, and that is not only tidiness:
          the detector costs 28 MB of WebAssembly and twelve inferences a
          second, and a mirror throws every one of them away. Skipping it is a
          quieter picture at the moment she can least afford a slow one. */}
      <CameraPreview
        onMountError={handleMountError}
        onReading={guiding ? handleReading : undefined}
        onDetectorUnavailable={guiding ? handleDetectorUnavailable : undefined}
      />

      {/* Absolutely positioned over the preview, never inside it. */}
      <View
        pointerEvents="box-none"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        }}
      >
        {!guiding /* A mirror carries no drawing at all. Not a silhouette to stand
             inside, not an outline, not a framing message — there is no
             detector behind any of them here, and a guide she cannot be given
             is worse than a picture that is honestly just a picture. */ ? null : phase ===
            "framing" && framing !== "unavailable" ? (
          /* No step diagram yet. She is being asked to point the phone at
             herself, and a drawing of the injection over a picture she has not
             finished aiming would be instructing her before she is ready. The
             outline shows what a good frame looks like; `framing` says whether
             she has one.

             Not drawn when nothing can be placed. A dashed shape to line
             yourself up with is an instruction from a guide, and on a phone
             with no detector there is no guide — the picture is a mirror and
             nothing more, which is what the card underneath says. Asking her to
             line herself up with something that will never respond is worse
             than showing her nothing.

             The outline and nothing else. The thigh's carried the injection
             band here for a commit, on the theory that where the needle goes
             is part of the target — and it is one instruction too many for a
             patient who is still moving a phone around looking for herself
             (Anaïs, 2026-09-24). This screen asks one thing: put your leg
             here. */
          <FramingSilhouette
            shape={site === "thigh" ? "thigh" : "torso"}
            facing={leg ? kneePointsTo(leg) : "right"}
            tone={
              framing === "ready"
                ? "rgba(130,212,168,0.95)"
                : "rgba(255,248,236,0.65)"
            }
          />
        ) : phase === "guiding" && figure === "inject" && zones ? (
          /* The injection step, and only it, and only with a reading in hand.
             The thigh had a drawn band standing in whenever no reading came,
             and two things were wrong with it that Anaïs saw at once: it sat
             there on steps that are not the injection, and being anchored to
             the drawing it did not move — a mark that claims to be on her leg
             and is not is worse than no mark, which is the position the abdomen
             has always taken. */
          <InjectionZones zones={zones} />
        ) : null}
      </View>

      <Pressable
        testID="injection-coach-turn-camera-off"
        accessibilityRole="button"
        accessibilityLabel={t(`${ns}.turn_the_camera_off`)}
        onPress={onTurnOff}
        hitSlop={10}
        style={({ pressed }) => ({
          position: "absolute",
          top: 10,
          right: 10,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          paddingVertical: 7,
          paddingHorizontal: 11,
          borderRadius: 999,
          backgroundColor: "rgba(28,18,14,0.62)",
          opacity: pressed ? 0.7 : 1,
        })}
      >
        <CameraOff size={14} color={semanticColors.onDark.text.primary} />
        {/* The label is worth its width on a desktop browser and costs too much
            on a phone, where the icon and its accessibility label carry it. */}
        {Platform.OS === "web" ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 12, color: semanticColors.onDark.text.primary }}
          >
            {t(`${ns}.turn_the_camera_off`)}
          </Text>
        ) : null}
      </Pressable>

      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: 10,
          bottom: 10,
          paddingVertical: 5,
          paddingHorizontal: 10,
          borderRadius: 999,
          backgroundColor: "rgba(28,18,14,0.62)",
        }}
      >
        {/* Said on the surface it is true of, not only in the sheet she read a
            minute ago. This is the moment the promise is load-bearing.

            And on a mirror, what the picture is for. Without it a camera that
            draws nothing reads as a camera that has failed — the outline is
            the thing this screen is known for, and its absence needs a reason
            on screen rather than in a release note. */}
        <Text
          className="font-dm-sans"
          style={{ fontSize: 11, color: semanticColors.onDark.text.primary }}
        >
          {guiding
            ? t(`${ns}.nothing_is_being_recorded`)
            : `${t(`${ns}.your_camera_as_a_mirror`)} · ${t(`${ns}.nothing_is_being_recorded`)}`}
        </Text>
      </View>
    </View>
  );
}
